'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { CheckCircle2, Clock, Megaphone } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { CardV2 } from '../CardV2'
import { ContentPanelV2 } from '../ContentPanelV2'
import { Timeline as TimelinePrimitive } from '../TimelinePrimitive'

// ============================================================================
// Context (List-level and Card-level)
// ============================================================================

/**
 * List-level context for tracking total items
 * Used to disable collapse behavior when only one notice exists
 */
interface ListContextValue {
  totalCount: number
}

const ListContext = createContext<ListContextValue | null>(null)

function useListContext(): ListContextValue | null {
  return useContext(ListContext)
}

/**
 * Card-level context for individual notice state
 */
interface CardContextValue {
  id: string
  isLatest: boolean
  /** Notice type for icon styling: 'amendment' | 'original' | undefined */
  noticeType?: 'amendment' | 'original'
  /** Whether this is the only card in the list (disables collapse) */
  isSingleItem: boolean
  /** Whether the card's collapsible content is currently expanded */
  isExpanded: boolean
  /** Callback to update expansion state (used by CardDescription) */
  setIsExpanded: (expanded: boolean) => void
}

const CardContext = createContext<CardContextValue | null>(null)

function useCardContext() {
  const context = useContext(CardContext)
  if (!context) {
    throw new Error('Card sub-components must be used within NoticesV2.Card')
  }
  return context
}

// ============================================================================
// NoticesV2 (Root)
// ============================================================================

interface NoticesV2Props {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2 - Root container for notice display (read-only)
 *
 * Simple wrapper for notice cards. No selection state - just displays notices.
 *
 * @example
 * ```tsx
 * <NoticesV2>
 *   <NoticesV2.Timeline>
 *     {notices.map((notice, idx) => (
 *       <NoticesV2.Card
 *         key={notice.id}
 *         id={notice.id}
 *         isLatest={idx === 0}
 *         noticeType={notice.status === 'Amended' ? 'amendment' : 'original'}
 *       >
 *         <NoticesV2.Card.HeaderSection>
 *           <NoticesV2.Card.Icon />
 *           <NoticesV2.Card.HeaderContent>
 *             <NoticesV2.Card.Title>{notice.title}</NoticesV2.Card.Title>
 *             <NoticesV2.Card.Meta>Posted {date} · {status}</NoticesV2.Card.Meta>
 *           </NoticesV2.Card.HeaderContent>
 *           <NoticesV2.Card.CurrentBadge />
 *         </NoticesV2.Card.HeaderSection>
 *         <NoticesV2.Card.BodySection>
 *           <NoticesV2.Card.Description>{notice.description}</NoticesV2.Card.Description>
 *           <NoticesV2.Card.MetadataGrid>
 *             <NoticesV2.Card.MetadataItem label="Due Date" value={date} />
 *             <NoticesV2.Card.MetadataItem label="Contact" value={contact} />
 *           </NoticesV2.Card.MetadataGrid>
 *         </NoticesV2.Card.BodySection>
 *       </NoticesV2.Card>
 *     ))}
 *   </NoticesV2.Timeline>
 * </NoticesV2>
 * ```
 */
const NoticesV2Root: React.FC<NoticesV2Props> = ({ children, className }) => {
  return <div className={className}>{children}</div>
}

NoticesV2Root.displayName = 'NoticesV2'

// ============================================================================
// NoticesV2.Timeline - Timeline layout with vertical connector
// ============================================================================

interface TimelineProps {
  children: React.ReactNode
  /** Total number of items (used to disable collapse when only 1 item) */
  count?: number
  className?: string
}

/**
 * NoticesV2.Timeline - Container for notice cards with consistent spacing
 *
 * Cards use colored left borders to indicate version status instead of external timeline.
 * Provides ListContext for child cards to know total count.
 */
const Timeline: React.FC<TimelineProps> = ({ children, count, className }) => {
  // Count children if count prop not provided
  const totalCount = count ?? React.Children.count(children)

  return (
    <ListContext.Provider value={{ totalCount }}>
      <div className={twMerge('flex flex-col gap-4', className)}>
        {children}
      </div>
    </ListContext.Provider>
  )
}

Timeline.displayName = 'NoticesV2.Timeline'

// ============================================================================
// NoticesV2.List (simple layout without timeline)
// ============================================================================

interface ListProps {
  children: React.ReactNode
  /** Total number of items (used to disable collapse when only 1 item) */
  count?: number
  className?: string
}

/**
 * NoticesV2.List - Simple container for notice cards with consistent spacing (no timeline)
 *
 * Provides ListContext for child cards to know total count.
 */
const List: React.FC<ListProps> = ({ children, count, className }) => {
  // Count children if count prop not provided
  const totalCount = count ?? React.Children.count(children)

  return (
    <ListContext.Provider value={{ totalCount }}>
      <div className={twMerge('flex flex-col gap-3', className)}>
        {children}
      </div>
    </ListContext.Provider>
  )
}

List.displayName = 'NoticesV2.List'

// ============================================================================
// NoticesV2.Card
// ============================================================================

interface CardProps {
  /** Unique identifier for this notice */
  id: string
  /** Whether this is the latest/current notice */
  isLatest?: boolean
  /** Notice type for icon styling */
  noticeType?: 'amendment' | 'original'
  /** Whether to show timeline connector (set automatically when inside Timeline) */
  showConnector?: boolean
  /** Whether this is the first card in the list (fades top of connector) */
  isFirst?: boolean
  /** Whether this is the last card in the list (hides bottom connector) */
  isLast?: boolean
  /** Whether this card is currently highlighted (from scroll-to action) */
  isHighlighted?: boolean
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card - Display container for a single notice (read-only)
 *
 * Built on CardV2 for consistent styling with colored left border
 * to indicate version status (accent for latest, muted for older).
 */
const Card: React.FC<CardProps> & {
  // Sub-components
  Header: typeof CardHeader
  Title: typeof CardTitle
  Meta: typeof CardMeta
  Description: typeof CardDescription
  CurrentBadge: typeof CurrentBadge
  Skeleton: typeof CardSkeleton
  // New structured layout components
  Icon: typeof CardIcon
  Label: typeof CardLabel
  HeaderSection: typeof CardHeaderSection
  HeaderContent: typeof CardHeaderContent
  BodySection: typeof CardBodySection
  MetadataGrid: typeof CardMetadataGrid
  MetadataItem: typeof CardMetadataItem
} = ({
  id,
  isLatest = false,
  noticeType,
  showConnector = false,
  isFirst = false,
  isLast = false,
  isHighlighted = false,
  children,
  className,
}) => {
  // Read list context to determine if this is the only item
  const listContext = useListContext()
  const isSingleItem = listContext ? listContext.totalCount === 1 : false

  // Track expansion state for collapsible content (rerender-functional-setstate)
  const [isExpanded, setIsExpandedRaw] = useState(false)
  const setIsExpanded = useCallback((expanded: boolean) => {
    setIsExpandedRaw(expanded)
  }, [])

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      id,
      isLatest,
      noticeType,
      isSingleItem,
      isExpanded,
      setIsExpanded,
    }),
    [id, isLatest, noticeType, isSingleItem, isExpanded, setIsExpanded],
  )

  return (
    <CardContext.Provider value={contextValue}>
      {/* Card Content - colored left border indicates version status */}
      <CardV2
        id={`notice-card-${id}`}
        variant="default"
        className={twMerge(
          'w-full transition-shadow duration-300',
          // Colored left border: accent for latest, muted for older
          isLatest
            ? 'border-l-4 border-l-accent-500'
            : 'border-l-4 border-l-primary-300 dark:border-l-primary-600',
          // Highlight ring when scrolled to
          isHighlighted && 'ring-2 ring-accent-400',
          className,
        )}
      >
        {children}
      </CardV2>
    </CardContext.Provider>
  )
}

Card.displayName = 'NoticesV2.Card'

// ============================================================================
// Card Sub-Components - Structured Layout
// ============================================================================

interface CardIconProps {
  /** Override the default icon */
  icon?: React.ElementType
  className?: string
}

/**
 * NoticesV2.Card.Icon - Type-based icon indicator (matches CardV2.HeaderIcon styling)
 *
 * Shows Megaphone for amendments (amber), CheckCircle2 for originals (emerald).
 * Automatically styled based on noticeType from context.
 * Small colored icon without background to match CardV2.HeaderIcon pattern.
 */
const CardIcon: React.FC<CardIconProps> = ({
  icon: IconOverride,
  className,
}) => {
  const { noticeType } = useCardContext()

  const isAmendment = noticeType === 'amendment'
  const Icon = IconOverride || (isAmendment ? Megaphone : CheckCircle2)

  return (
    <span
      className={twMerge(
        'flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-full [&>svg]:w-full',
        isAmendment
          ? 'text-amber-500 dark:text-amber-400'
          : 'text-emerald-500 dark:text-emerald-400',
        className,
      )}
    >
      <Icon />
    </span>
  )
}

CardIcon.displayName = 'NoticesV2.Card.Icon'

interface CardLabelProps {
  children?: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.Label - Uppercase section label (matches CardV2.HeaderTitle styling)
 *
 * Shows "AMENDMENT" or "NOTICE" based on noticeType from context.
 * Can be overridden by passing children.
 */
const CardLabel: React.FC<CardLabelProps> = ({ children, className }) => {
  const { noticeType } = useCardContext()

  const label =
    children || (noticeType === 'amendment' ? 'Amendment' : 'Notice')

  return (
    <h3
      className={twMerge(
        'text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {label}
    </h3>
  )
}

CardLabel.displayName = 'NoticesV2.Card.Label'

interface CardHeaderSectionProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.HeaderSection - Header area matching CardV2 styling
 *
 * Uses the same CSS classes as CardV2.Header for visual consistency.
 * Layout: Icon + Label on top row, Title/Meta + Badge on content row.
 */
const CardHeaderSection: React.FC<CardHeaderSectionProps> = ({
  children,
  className,
}) => {
  // Separate children into structured parts
  const childArray = React.Children.toArray(children)
  const icon = childArray.find(
    (child) => React.isValidElement(child) && child.type === CardIcon,
  )
  const label = childArray.find(
    (child) => React.isValidElement(child) && child.type === CardLabel,
  )
  const badge = childArray.find(
    (child) => React.isValidElement(child) && child.type === CurrentBadge,
  )
  // Content (HeaderContent with Title/Meta) goes alongside badge
  const content = childArray.filter(
    (child) =>
      !React.isValidElement(child) ||
      (child.type !== CardIcon &&
        child.type !== CardLabel &&
        child.type !== CurrentBadge),
  )

  // Match CardV2.Header structure and styling exactly
  return (
    <div
      className={twMerge(
        'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
        className,
      )}
    >
      {/* Top row: Icon + Label + Badge (mobile only) */}
      <div className="flex items-center gap-1.5">
        {icon}
        {label}
        {/* Badge in top row on mobile */}
        {badge && <div className="shrink-0 sm:hidden">{badge}</div>}
      </div>
      {/* Content row: Title/Meta | Badge (desktop only) */}
      {(content.length > 0 || badge) && (
        <div className="mt-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">{content}</div>
          {/* Badge next to title on desktop */}
          {badge && (
            <div className="hidden shrink-0 pt-0.5 sm:block">{badge}</div>
          )}
        </div>
      )}
    </div>
  )
}

CardHeaderSection.displayName = 'NoticesV2.Card.HeaderSection'

interface CardHeaderContentProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.HeaderContent - Wrapper for title and meta inside header
 */
const CardHeaderContent: React.FC<CardHeaderContentProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('flex-1 min-w-0', className)}>{children}</div>
}

CardHeaderContent.displayName = 'NoticesV2.Card.HeaderContent'

interface CardBodySectionProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.BodySection - Body area matching CardV2.Content styling
 *
 * Uses the same CSS classes as CardV2.Content for visual consistency.
 */
const CardBodySection: React.FC<CardBodySectionProps> = ({
  children,
  className,
}) => {
  // Match CardV2.Content padding (p-4) plus notice-specific spacing
  return <div className={twMerge('space-y-3 p-4', className)}>{children}</div>
}

CardBodySection.displayName = 'NoticesV2.Card.BodySection'

interface CardMetadataGridProps {
  children: React.ReactNode
  /** Hide top border (use when there's no content above) */
  noBorder?: boolean
  className?: string
}

/**
 * NoticesV2.Card.MetadataGrid - Grid container for metadata items
 */
const CardMetadataGrid: React.FC<CardMetadataGridProps> = ({
  children,
  noBorder = false,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'grid grid-cols-1 gap-2.5 sm:grid-cols-2',
        !noBorder &&
          'border-t border-primary-100 pt-3 dark:border-primary-800/50',
        className,
      )}
    >
      {children}
    </div>
  )
}

CardMetadataGrid.displayName = 'NoticesV2.Card.MetadataGrid'

interface CardMetadataItemProps {
  label: string
  value: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.MetadataItem - Single metadata field with label and value
 */
const CardMetadataItem: React.FC<CardMetadataItemProps> = ({
  label,
  value,
  className,
}) => {
  if (!value) return null

  return (
    <div className={className}>
      <span className="mb-0.5 block text-sm font-bold uppercase tracking-wider text-primary-400 dark:text-primary-500">
        {label}
      </span>
      <span className="block text-sm text-primary-700 dark:text-primary-300">
        {value}
      </span>
    </div>
  )
}

CardMetadataItem.displayName = 'NoticesV2.Card.MetadataItem'

// ============================================================================
// Card Sub-Components - Original (backward compatible)
// ============================================================================

interface CardHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.Header - Flex row for badges and meta info (simple layout)
 */
const CardHeader: React.FC<CardHeaderProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge('mb-2 flex flex-wrap items-center gap-2', className)}
    >
      {children}
    </div>
  )
}

CardHeader.displayName = 'NoticesV2.Card.Header'

interface CardTitleProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.Title - Styled title slot
 */
const CardTitle: React.FC<CardTitleProps> = ({ children, className }) => {
  return (
    <h4
      className={twMerge(
        'text-sm font-semibold text-primary-900 dark:text-primary-100',
        className,
      )}
    >
      {children}
    </h4>
  )
}

CardTitle.displayName = 'NoticesV2.Card.Title'

interface CardMetaProps {
  children: React.ReactNode
  className?: string
}

/**
 * NoticesV2.Card.Meta - Muted text for dates and secondary info
 */
const CardMeta: React.FC<CardMetaProps> = ({ children, className }) => {
  return (
    <p
      className={twMerge(
        'mt-0.5 text-sm text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {children}
    </p>
  )
}

CardMeta.displayName = 'NoticesV2.Card.Meta'

interface CardDescriptionProps {
  children: React.ReactNode
  /** Max lines to show before truncation. Defaults to 3. Set to 0 to disable truncation. */
  lines?: number
  /** Render children as markdown. Defaults to false. */
  markdown?: boolean
  className?: string
}

/**
 * NoticesV2.Card.Description - Truncatable description slot with expand/collapse
 *
 * Delegates to ContentPanelV2.Content for collapsible functionality,
 * ensuring consistent behavior and code reuse.
 *
 * When there is only one notice in the list, collapse is disabled and content
 * is shown expanded (pure, idempotent rendering based on context state).
 */
const CardDescription: React.FC<CardDescriptionProps> = ({
  children,
  lines = 3,
  markdown = false,
  className,
}) => {
  const { isSingleItem, setIsExpanded } = useCardContext()

  // When there's only one notice, disable collapse (show full content)
  // Otherwise, use the lines prop for collapsible behavior
  const collapsibleLines = isSingleItem
    ? undefined
    : lines > 0
      ? lines
      : undefined

  return (
    <ContentPanelV2 size="compact" variant="inline">
      <ContentPanelV2.Content
        markdown={markdown}
        collapsible={collapsibleLines}
        onExpandedChange={setIsExpanded}
        className={twMerge(
          'whitespace-pre-wrap text-sm leading-relaxed text-primary-600 dark:text-primary-200',
          className,
        )}
      >
        {children}
      </ContentPanelV2.Content>
    </ContentPanelV2>
  )
}

CardDescription.displayName = 'NoticesV2.Card.Description'

interface CurrentBadgeProps {
  className?: string
}

/**
 * NoticesV2.Card.CurrentBadge - Auto-renders "Current Version" if this is the latest notice
 */
const CurrentBadge: React.FC<CurrentBadgeProps> = ({ className }) => {
  const { isLatest } = useCardContext()

  if (!isLatest) {
    return null
  }

  return (
    <span
      className={twMerge(
        'relative -top-0.5 inline rounded bg-accent-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-accent-700 dark:bg-accent-900/40 dark:text-accent-400 sm:top-0 sm:px-2 sm:py-0.5 sm:text-xs',
        className,
      )}
    >
      Current
      <span className="hidden sm:inline">&nbsp;Version</span>
    </span>
  )
}

CurrentBadge.displayName = 'NoticesV2.Card.CurrentBadge'

// ============================================================================
// Skeletons
// ============================================================================

interface CardSkeletonProps {
  className?: string
}

/**
 * NoticesV2.Card.Skeleton - Loading skeleton for a notice card
 *
 * Uses CardV2 container with direct CSS matching CardV2 header/content styling.
 * Matches new layout: Icon + Label row, then Title/Meta + Badge row below.
 */
const CardSkeleton: React.FC<CardSkeletonProps> = ({ className }) => {
  return (
    <CardV2 variant="default" className={twMerge('w-full', className)}>
      {/* Header skeleton matching CardV2.Header styling with new layout */}
      <div className="border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50">
        {/* Top row: Icon + Label */}
        <div className="flex items-center gap-1.5">
          <div className="h-4 w-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-2.5 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
        </div>
        {/* Content row: Title/Meta | Badge */}
        <div className="mt-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="h-3.5 w-3/4 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div className="h-2.5 w-1/3 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          </div>
          <div className="h-5 w-24 shrink-0 animate-pulse rounded-sm bg-primary-200 dark:bg-primary-700" />
        </div>
      </div>
      {/* Body skeleton matching CardV2.Content styling */}
      <div className="space-y-4 p-4">
        <div className="space-y-1.5">
          <div className="h-3 w-full animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-3 w-5/6 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
        </div>
        {/* Metadata grid skeleton */}
        <div className="grid grid-cols-2 gap-3 border-t border-primary-100 pt-4 dark:border-primary-800/50">
          <div>
            <div className="mb-0.5 h-2 w-12 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div className="h-3 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          </div>
          <div>
            <div className="mb-0.5 h-2 w-12 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div className="h-3 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          </div>
        </div>
      </div>
    </CardV2>
  )
}

CardSkeleton.displayName = 'NoticesV2.Card.Skeleton'

interface ListSkeletonProps {
  /** Number of card skeletons to render */
  count?: number
  className?: string
}

/**
 * NoticesV2.List.Skeleton - Loading skeleton for a list of notices
 */
const ListSkeleton: React.FC<ListSkeletonProps> = ({
  count = 3,
  className,
}) => {
  return (
    <div className={twMerge('flex flex-col gap-3', className)}>
      {[...Array(count)].map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  )
}

ListSkeleton.displayName = 'NoticesV2.List.Skeleton'

// ============================================================================
// Sidebar Timeline Widget
// ============================================================================

type TimelineEventType =
  | 'posted'
  | 'amended'
  | 'amended-latest'
  | 'q-and-a'
  | 'deadline'
  | 'expiry'
  | 'award'

interface TimelineEventProps {
  date: string
  title: React.ReactNode
  type: TimelineEventType
  /** Optional icon to display next to the title */
  icon?: React.ElementType
  /** Visual emphasis when value changed from previous version */
  highlight?: boolean
  className?: string
  onClick?: () => void
}

const timelineEventStyles: Record<
  TimelineEventType,
  { dot: string; label: string }
> = {
  posted: {
    dot: 'border-emerald-500 bg-emerald-100 dark:bg-emerald-900',
    label: 'text-emerald-600 dark:text-emerald-400',
  },
  amended: {
    dot: 'border-amber-500 bg-amber-100 dark:bg-amber-900',
    label: 'text-amber-600 dark:text-amber-400',
  },
  'amended-latest': {
    dot: 'border-amber-600 bg-amber-200 dark:bg-amber-800 ring-2 ring-amber-400/50',
    label: 'text-amber-700 dark:text-amber-300 font-semibold',
  },
  'q-and-a': {
    dot: 'border-violet-500 bg-violet-100 dark:bg-violet-900',
    label: 'text-violet-600 dark:text-violet-400',
  },
  deadline: {
    dot: 'border-rose-500 bg-rose-100 dark:bg-rose-900',
    label: 'text-rose-600 dark:text-rose-400',
  },
  expiry: {
    dot: 'border-primary-400 bg-primary-100 dark:border-primary-500 dark:bg-primary-800',
    label: 'text-primary-500 dark:text-primary-400',
  },
  award: {
    dot: 'border-sky-500 bg-sky-100 dark:bg-sky-900',
    label: 'text-sky-600 dark:text-sky-400',
  },
}

// NoticesV2 uses smaller dots (h-2.5 w-2.5 = 10px) at top-1 (4px)
// So center is at 4px + 5px = 9px
const NOTICES_DOT_CENTER_Y = 9
// Line at center of dot (dot is 10px wide at left-0.5, line at 7px)
const NOTICES_LINE_X = 7

/**
 * NoticesV2.TimelineEvent - Single event in a sidebar timeline
 *
 * Uses Timeline primitive for consistent line alignment.
 */
const TimelineEvent: React.FC<TimelineEventProps> = ({
  date,
  title,
  type,
  icon: Icon,
  highlight = false,
  className,
  onClick,
}) => {
  const styles = timelineEventStyles[type]
  const isClickable = Boolean(onClick)

  const handleKeyDown = isClickable
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }
    : undefined

  const displayLabel =
    type === 'amended-latest' ? 'amended' : type === 'q-and-a' ? 'Q&A' : type

  return (
    <TimelinePrimitive.Item
      dotCenterY={NOTICES_DOT_CENTER_Y}
      lineX={NOTICES_LINE_X}
      lineWidth={1}
      className={twMerge(
        'group pl-6',
        isClickable && 'cursor-pointer',
        className,
      )}
    >
      <div
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="contents"
      >
        {/* Dot with optional icon */}
        {Icon ? (
          <span
            className={twMerge(
              'absolute left-0.5 top-0.5 z-10 flex h-3 w-3 items-center justify-center rounded-full bg-white transition-transform dark:bg-primary-900',
              isClickable && 'group-hover:scale-110',
              styles.label,
            )}
          >
            <Icon className="h-full w-full" />
          </span>
        ) : (
          <TimelinePrimitive.Dot
            className={twMerge(
              'left-0.5 top-1 h-2.5 w-2.5 border-2 transition-transform',
              isClickable && 'group-hover:scale-110',
              styles.dot,
            )}
          />
        )}
        <TimelinePrimitive.Content>
          <span
            className={twMerge(
              'mb-0.5 text-sm font-bold uppercase tracking-wide',
              styles.label,
            )}
          >
            {displayLabel}
          </span>
          <span
            className={twMerge(
              'text-sm font-medium text-primary-800 dark:text-primary-200',
              isClickable &&
                'group-hover:text-accent-600 dark:group-hover:text-accent-400',
            )}
          >
            {title}
          </span>
          <span
            className={twMerge(
              'mt-0.5 font-mono text-sm text-primary-500 dark:text-primary-400',
              highlight &&
                'font-semibold text-amber-600 underline decoration-amber-400 dark:text-amber-400',
            )}
          >
            {date}
          </span>
        </TimelinePrimitive.Content>
      </div>
    </TimelinePrimitive.Item>
  )
}

TimelineEvent.displayName = 'NoticesV2.TimelineEvent'

interface SidebarTimelineProps {
  children: React.ReactNode
  title?: string
  className?: string
}

/**
 * NoticesV2.SidebarTimeline - Container for timeline events in sidebar
 *
 * Built on CardV2 for consistent styling. Wraps children in Timeline.Root
 * to provide position context for TimelineEvent items.
 */
const SidebarTimeline: React.FC<SidebarTimelineProps> = ({
  children,
  title = 'Project Timeline',
  className,
}) => (
  <CardV2 variant="default" className={className}>
    <CardV2.Header>
      <CardV2.HeaderIcon>
        <Clock />
      </CardV2.HeaderIcon>
      <CardV2.HeaderTitle>{title}</CardV2.HeaderTitle>
    </CardV2.Header>
    <CardV2.Content>
      <TimelinePrimitive.Root>{children}</TimelinePrimitive.Root>
    </CardV2.Content>
  </CardV2>
)

SidebarTimeline.displayName = 'NoticesV2.SidebarTimeline'

// ============================================================================
// Attach Sub-Components
// ============================================================================

Card.Header = CardHeader
Card.Title = CardTitle
Card.Meta = CardMeta
Card.Description = CardDescription
Card.CurrentBadge = CurrentBadge
Card.Skeleton = CardSkeleton
// New structured layout components
Card.Icon = CardIcon
Card.Label = CardLabel
Card.HeaderSection = CardHeaderSection
Card.HeaderContent = CardHeaderContent
Card.BodySection = CardBodySection
Card.MetadataGrid = CardMetadataGrid
Card.MetadataItem = CardMetadataItem

const ListWithSkeleton = List as typeof List & {
  Skeleton: typeof ListSkeleton
}
ListWithSkeleton.Skeleton = ListSkeleton

// ============================================================================
// Export
// ============================================================================

const NoticesV2 = NoticesV2Root as typeof NoticesV2Root & {
  List: typeof ListWithSkeleton
  Timeline: typeof Timeline
  Card: typeof Card
  SidebarTimeline: typeof SidebarTimeline
  TimelineEvent: typeof TimelineEvent
}

NoticesV2.List = ListWithSkeleton
NoticesV2.Timeline = Timeline
NoticesV2.Card = Card
NoticesV2.SidebarTimeline = SidebarTimeline
NoticesV2.TimelineEvent = TimelineEvent

export { NoticesV2, useCardContext }
export type {
  NoticesV2Props,
  ListProps as NoticesListProps,
  TimelineProps as NoticesTimelineProps,
  CardProps as NoticesCardProps,
  CardHeaderProps as NoticesCardHeaderProps,
  CardTitleProps as NoticesCardTitleProps,
  CardMetaProps as NoticesCardMetaProps,
  CardDescriptionProps as NoticesCardDescriptionProps,
  CardSkeletonProps as NoticesCardSkeletonProps,
  ListSkeletonProps as NoticesListSkeletonProps,
  CardIconProps as NoticesCardIconProps,
  CardLabelProps as NoticesCardLabelProps,
  CardHeaderSectionProps as NoticesCardHeaderSectionProps,
  CardHeaderContentProps as NoticesCardHeaderContentProps,
  CardBodySectionProps as NoticesCardBodySectionProps,
  CardMetadataGridProps as NoticesCardMetadataGridProps,
  CardMetadataItemProps as NoticesCardMetadataItemProps,
  TimelineEventType,
  TimelineEventProps,
  SidebarTimelineProps,
}
